import { useState, type ReactNode } from "react";
import { cn } from "@/cn";
import type { ProviderConfig } from "@/api/providers";
import { isScannableBoardProvider, isScannableAtsProvider } from "@/lib/provider-ui";
import {
  TYPE_LABELS,
  TYPE_COLORS,
  featureLabel,
  typeHelpText,
  hasIntegrationDetails,
  hasConfigData,
  formatProviderDate,
} from "@/lib/provider-ui";

export function CopyIdButton({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground transition hover:text-foreground"
      title="Copy provider ID"
    >
      {copied ? "Copied!" : `ID: ${id}`}
    </button>
  );
}

function ConfigBlock({ label, data }: { label: string; data: Record<string, unknown> }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </h3>
      <pre className="overflow-x-auto rounded bg-muted px-3 py-2 text-xs text-foreground">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

function MetaSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  );
}

export function ProviderHeroCard({
  provider,
  trailing,
  badges,
}: {
  provider: ProviderConfig;
  trailing?: ReactNode;
  badges?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold text-foreground">{provider.name}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {provider.description || "No description available."}
          </p>
        </div>
        {trailing}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <CopyIdButton id={provider.providerId} />
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-[10px] font-medium",
            TYPE_COLORS[provider.type],
          )}
        >
          {TYPE_LABELS[provider.type]}
        </span>
        {badges}
      </div>
    </div>
  );
}

export function ProviderMetadataSections({ provider }: { provider: ProviderConfig }) {
  const createdLabel = formatProviderDate(provider.createdAt);
  const updatedLabel = formatProviderDate(provider.updatedAt);

  return (
    <>
      {provider.features && provider.features.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-5">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Capabilities
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {provider.features.map((f) => (
              <span
                key={f}
                className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground"
              >
                {featureLabel(f)}
              </span>
            ))}
          </div>
        </div>
      )}

      {hasIntegrationDetails(provider) ? (
        <div className="rounded-lg border border-border bg-card p-5">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Integration
          </h3>
          <div className="space-y-4">
            {provider.urlPattern && (
              <div>
                <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  URL pattern
                </p>
                <code className="block break-all rounded bg-muted px-3 py-2 text-xs text-foreground">
                  {provider.urlPattern}
                </code>
              </div>
            )}
            {provider.allowedHosts && provider.allowedHosts.length > 0 && (
              <div>
                <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Trusted hosts
                </p>
                <ul className="list-inside list-disc space-y-0.5">
                  {provider.allowedHosts.map((host) => (
                    <li key={host} className="text-xs text-muted-foreground">
                      {host}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              {provider.supportsDetect !== undefined && (
                <div>
                  <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    Auto-detect
                  </p>
                  <p
                    className={cn(
                      "text-sm",
                      provider.supportsDetect ? "text-success" : "text-muted-foreground",
                    )}
                  >
                    {provider.supportsDetect ? "Supported" : "Not supported"}
                  </p>
                </div>
              )}
              {provider.supportsApiUrl !== undefined && (
                <div>
                  <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    API URL override
                  </p>
                  <p
                    className={cn(
                      "text-sm",
                      provider.supportsApiUrl ? "text-success" : "text-muted-foreground",
                    )}
                  >
                    {provider.supportsApiUrl ? "Supported" : "Not supported"}
                  </p>
                </div>
              )}
              {provider.timeoutMs !== undefined && (
                <div>
                  <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    Timeout
                  </p>
                  <p className="text-sm text-foreground">
                    {(provider.timeoutMs / 1000).toFixed(0)}s
                  </p>
                </div>
              )}
              {provider.retryPolicy && (
                <div>
                  <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    Retry policy
                  </p>
                  <p className="text-sm text-foreground">{provider.retryPolicy}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card p-5">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Integration
          </h3>
          <p className="text-sm text-muted-foreground">
            No integration metadata is available for this provider yet.{" "}
            {typeHelpText(provider.type)}
          </p>
        </div>
      )}

      {hasConfigData(provider.defaultConfig) && (
        <ConfigBlock label="Default configuration" data={provider.defaultConfig} />
      )}
      {hasConfigData(provider.userOverrides) && (
        <ConfigBlock label="User overrides" data={provider.userOverrides} />
      )}

      {(createdLabel || updatedLabel) && (
        <div className="rounded-lg border border-border bg-card p-5">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Metadata
          </h3>
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            {createdLabel && (
              <div>
                <dt className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Created
                </dt>
                <dd className="text-foreground">{createdLabel}</dd>
              </div>
            )}
            {updatedLabel && (
              <div>
                <dt className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Updated
                </dt>
                <dd className="text-foreground">{updatedLabel}</dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </>
  );
}

export function ProviderHowItWorks({ type }: { type: ProviderConfig["type"] }) {
  return (
    <MetaSection title="How it works">
      <p className="text-xs text-muted-foreground">{typeHelpText(type)}</p>
    </MetaSection>
  );
}

export function canSelectForScan(provider: ProviderConfig): boolean {
  return isScannableBoardProvider(provider);
}
